import { globals } from './globals';

type StringAndNumber = string | number;

/**
 * Converts a price (in local currency) to Velocity points.
 * Formula: points = price × pointsPerDollar (rounded to nearest whole number)
 * Bonus points are NOT included (confirmed out of scope).
 */
export const convertPriceToPoints = (price: StringAndNumber): number => {
  const numericPrice = Number(price);
  const pointsPerDollar = Number(globals.pointsPerDollar);
  if (!numericPrice || numericPrice <= 0 || !Number.isFinite(pointsPerDollar) || pointsPerDollar <= 0) {
    return 0;
  }
  return Math.ceil(numericPrice * pointsPerDollar);
};

/**
 * Formats points as a display string with thousands separator and label.
 * e.g. 1500 → "1,500 pts"
 */
export const formatPoints = (points: number): string => {
  const label = globals.pointsLabel || 'pts';
  return `${points.toLocaleString('en-AU')} ${label}`;
};

/**
 * Converts a price to points and returns the formatted display string.
 * Convenience function combining convertPriceToPoints + formatPoints.
 */
export const formatPriceAsPoints = (price: StringAndNumber): string => {
  const points = convertPriceToPoints(price);
  return formatPoints(points);
};

/**
 * Returns true if the current site is a redemption/points site.
 */
export const isRedemptionSite = (): boolean => globals.brandId === 'vws' && globals.pointsPerDollar > 0;

/**
 * Formats the user's points balance for display, with thousands separator.
 * Returns empty string if pointsBalance is undefined.
 */
export const formatUserPointsBalance = (pointsBalance: number | undefined): string => {
  if (pointsBalance === undefined) {
    return '';
  }
  return new Intl.NumberFormat('en-AU', {
    maximumFractionDigits: 0,
  }).format(pointsBalance);
};

export const formatPriceToTwoDecimals = (price?: StringAndNumber, prec = 2): string => {
  const numericPrice = typeof price === 'string' ? Number(price) : price;

  if (typeof numericPrice === 'number' && !Number.isNaN(numericPrice)) {
    return numericPrice.toFixed(prec);
  }

  return (0).toFixed(prec);
};

export const formatPrice = (price?: StringAndNumber, prec?: number): string =>
  `${globals.currency || ''}${formatPriceToTwoDecimals(price, prec)}`;

export const priceAndPointConverter = (price: StringAndNumber, prec?: number): string => {
  if (isRedemptionSite()) {
    return formatPriceAsPoints(price);
  }
  return formatPrice(price, prec);
};
